import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X, Calculator } from 'lucide-react';

const ScoreBreakdownModal = ({ isOpen, onClose, score, dimension = null, fullScores = null }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const renderOIBreakdown = () => {
    if (!fullScores) return null;

    const { novelty, resonance, synthesis, ethical } = fullScores;
    const total = (0.3 * novelty) + (0.3 * resonance) + (0.2 * synthesis) + (0.2 * ethical);

    return (
      <div className="space-y-4">
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h4 className="font-semibold text-purple-900 mb-2">Formula:</h4>
          <p className="text-sm font-mono text-purple-800">
            OI = (0.3 × Novelty) + (0.3 × Resonance) + (0.2 × Synthesis) + (0.2 × Ethical)
          </p>
        </div>

        <div className="space-y-2">
          <h4 className="font-semibold text-slate-900">Calculation:</h4>
          
          <div className="space-y-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-purple-700">Novelty (30%)</span>
              <span className="font-mono">0.3 × {novelty.toFixed(1)} = {(0.3 * novelty).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-blue-700">Resonance (30%)</span>
              <span className="font-mono">0.3 × {resonance.toFixed(1)} = {(0.3 * resonance).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-green-700">Synthesis (20%)</span>
              <span className="font-mono">0.2 × {synthesis.toFixed(1)} = {(0.2 * synthesis).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-orange-700">Ethical (20%)</span>
              <span className="font-mono">0.2 × {ethical.toFixed(1)} = {(0.2 * ethical).toFixed(2)}</span>
            </div>
            
            <div className="border-t-2 border-slate-300 my-2 pt-2">
              <div className="flex justify-between items-center font-bold text-lg">
                <span className="text-slate-900">Total OI Score</span>
                <span className="font-mono text-emerald-600">{total.toFixed(2)}/10</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">Why These Weights?</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Novelty (30%)</strong>: How unprecedented your idea is</li>
            <li>• <strong>Resonance (30%)</strong>: How well it solves real problems</li>
            <li>• <strong>Synthesis (20%)</strong>: Cross-disciplinary integration</li>
            <li>• <strong>Ethical (20%)</strong>: Responsible and inclusive design</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderDimensionBreakdown = () => {
    const dimensionInfo = {
      novelty: {
        name: 'Novelty',
        colorBg: 'bg-purple-50',
        colorBorder: 'border-purple-200',
        colorText: 'text-purple-900',
        colorTextLight: 'text-purple-700',
        colorBar: 'bg-purple-500',
        weight: '30%',
        description: 'Measures how unprecedented and unique your approach is. High novelty means you\'re breaking new ground.',
        factors: [
          'Uniqueness of approach',
          'Departure from existing solutions',
          'Creative combination of ideas',
          'Novel methodology or framework'
        ]
      },
      resonance: {
        name: 'Resonance',
        colorBg: 'bg-blue-50',
        colorBorder: 'border-blue-200',
        colorText: 'text-blue-900',
        colorTextLight: 'text-blue-700',
        colorBar: 'bg-blue-500',
        weight: '30%',
        description: 'Evaluates how well your solution addresses real needs and problems. Strong resonance means clear problem-solution fit.',
        factors: [
          'Clarity of problem definition',
          'Strength of problem-solution fit',
          'Stakeholder alignment',
          'Market or user demand'
        ]
      },
      synthesis: {
        name: 'Synthesis',
        colorBg: 'bg-green-50',
        colorBorder: 'border-green-200',
        colorText: 'text-green-900',
        colorTextLight: 'text-green-700',
        colorBar: 'bg-green-500',
        weight: '20%',
        description: 'Assesses how effectively you combine concepts from multiple disciplines. Higher synthesis shows interdisciplinary integration.',
        factors: [
          'Cross-disciplinary connections',
          'Integration of diverse concepts',
          'Emergent properties from combination',
          'Holistic thinking'
        ]
      },
      ethical: {
        name: 'Ethical Anchoring',
        colorBg: 'bg-orange-50',
        colorBorder: 'border-orange-200',
        colorText: 'text-orange-900',
        colorTextLight: 'text-orange-700',
        colorBar: 'bg-orange-500',
        weight: '20%',
        description: 'Measures how responsibly and inclusively your solution is designed. Strong ethics means careful impact consideration.',
        factors: [
          'Consideration of positive impacts',
          'Awareness of negative consequences',
          'Stakeholder inclusion',
          'Sustainability and long-term thinking'
        ]
      }
    };

    const info = dimensionInfo[dimension];
    if (!info) return null;

    return (
      <div className="space-y-4">
        <div className={`${info.colorBg} p-4 rounded-lg border ${info.colorBorder}`}>
          <div className="flex justify-between items-start mb-2">
            <h4 className={`font-semibold ${info.colorText}`}>{info.name}</h4>
            <span className={`text-sm font-bold ${info.colorTextLight} bg-white px-2 py-1 rounded`}>
              Weight: {info.weight}
            </span>
          </div>
          <p className="text-sm text-slate-700">{info.description}</p>
        </div>

        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="flex justify-between items-center mb-3">
            <span className="font-semibold text-slate-900">Your Score:</span>
            <span className="text-2xl font-bold text-emerald-600">{score}/10</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
            <div 
              className={`${info.colorBar} h-full transition-all duration-500`}
              style={{ width: `${(score / 10) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">What This Dimension Measures:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            {info.factors.map((factor, idx) => (
              <li key={idx}>• {factor}</li>
            ))}
          </ul>
        </div>

        <div className="text-xs text-slate-500 text-center">
          Contribution to overall OI: {(score * (parseFloat(info.weight) / 100)).toFixed(2)} points
        </div>
      </div>
    );
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-2xl relative max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10 p-1 rounded-full hover:bg-slate-100"
        >
          <X className="w-5 h-5" />
        </button>

        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Calculator className="w-6 h-6 text-purple-600" />
            {dimension ? `${dimension.charAt(0).toUpperCase() + dimension.slice(1)} Score Breakdown` : 'OI Score Breakdown'}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {dimension ? renderDimensionBreakdown() : renderOIBreakdown()}
        </CardContent>
      </Card>
    </div>
  );

  // Use createPortal to render modal at document.body level
  return createPortal(modalContent, document.body);
};

export default ScoreBreakdownModal;
